import {
  DOCUMENT_SIGNATURE_PREFIX,
  LIMITS,
  anchorSigner,
  isSenderSignature,
  isSenderText,
  typedTextSize,
  type SignatureAnchor,
} from '@worki/shared';

/**
 * SIG-003 — the geometry of a placed signature box, away from the DOM.
 *
 * Its own module so it can be tested without a browser: the placer imports the
 * app's i18n, which reaches for `document` the moment it loads.
 */

/**
 * The same box, moved by a fraction of the page and kept on it.
 *
 * Fractions rather than pixels, for the reason the anchor is stored as one: the
 * gesture happens on a preview scaled to whatever screen it is, and the stamp
 * happens at the page's real size. The clamp holds the far edge inside the
 * page, and `Math.max(…, 0)` covers a stored box wider than the page it claims
 * to be on — the server refuses a negative coordinate, and a drag of one odd
 * row should not be the thing that produces one.
 */
export function moveAnchor(anchor: SignatureAnchor, dx: number, dy: number): SignatureAnchor {
  return {
    ...anchor,
    x: Math.min(Math.max(anchor.x + dx, 0), Math.max(1 - anchor.width, 0)),
    y: Math.min(Math.max(anchor.y + dy, 0), Math.max(1 - anchor.height, 0)),
  };
}

/**
 * The list, with the box being dragged moved to where the finger is now.
 *
 * Both the travel and the box it applies to are counted from the moment the
 * finger went down, never from the event before. A pointermove is not a gesture
 * React redraws between — several can arrive on the same list — so a handler
 * that read the box back out of that list would measure each step against a
 * position two steps old and drop the difference on the floor.
 *
 * Counting from the origin also means the clamp cannot eat travel: dragging
 * into a corner and back out again returns the box to where it started, because
 * nothing was ever stored except where the finger began.
 */
export function dragAnchor(
  anchors: SignatureAnchor[],
  index: number,
  origin: SignatureAnchor,
  dx: number,
  dy: number,
): SignatureAnchor[] {
  return anchors.map((current, at) => (at === index ? moveAnchor(origin, dx, dy) : current));
}

/**
 * The frame a typed box draws inside its own edges (`border-2 px-1`), in pixels:
 * across is both sides' border and padding, down is both borders.
 */
export const TYPED_INSET = { across: 12, down: 4 };

/**
 * A canvas measures the font the box is drawn in, but a browser lays out an
 * input with its own rounding, and a line measured to the pixel scrolls by one.
 */
const MEASURE_SLACK = 0.94;

/**
 * 1.1 — the font size for a line of typing in a box on the page, in pixels.
 *
 * Fitted to the line and not only to the box. Set from the box's height alone,
 * a box turned from a signature into text kept a signature's height and held
 * about seven letters; the rest of the answer scrolled out of sight inside the
 * input (Nitzan, 16.09). The fit is the stamp's own rule, so the line on screen
 * is the line on the PDF. There is no smallest size: a size that stopped
 * shrinking would be a line cut off again, and a long answer in a small box
 * prints small too — the + button makes the room for it.
 *
 * `widthAtOne` is the line's width at one pixel; nothing to measure is sized
 * by the height alone.
 */
export function typedFontSize(box: { width: number; height: number }, widthAtOne: number): number {
  return typedTextSize(
    {
      width: Math.max(box.width - TYPED_INSET.across, 0) * MEASURE_SLACK,
      height: Math.max(box.height - TYPED_INSET.down, 0),
    },
    widthAtOne,
  );
}

/**
 * 1.1 — how a box is drawn on the page, for whoever is looking at it.
 *
 * - `place`: a box standing for what goes there later — tapped, moved, resized.
 * - `answer`: a blank a signer types into on the signing page.
 * - `sender-input`: a blank the sender fills in, typed into on the screen that
 *   places it, because that is where the sender sees what the line will say.
 * - `sender-text`: the sender's words everywhere else, as fixed text. A signer
 *   is not asked for them and cannot change them, and once the document is out
 *   the sender cannot either.
 * - `sender-sign`: the sender's own signature on the screen that places it. A
 *   box like any other there — tapped, moved, resized — with the drawing in it.
 * - `sender-signature`: the same drawing everywhere else, as part of the page.
 *
 * `placing` is the sender's screen while the document can still change;
 * `answering` is the signing page while this visitor can still answer.
 */
export type BoxView = 'place' | 'answer' | 'sender-input' | 'sender-text' | 'sender-sign' | 'sender-signature';

export function boxView(anchor: SignatureAnchor, screen: { placing: boolean; answering: boolean }): BoxView {
  if (isSenderText(anchor)) return screen.placing ? 'sender-input' : 'sender-text';
  if (isSenderSignature(anchor)) return screen.placing ? 'sender-sign' : 'sender-signature';
  return anchor.kind === 'text' && screen.answering ? 'answer' : 'place';
}

/**
 * 1.1, redone — what a place on the page is for: the four options the dialog
 * offers when a box is placed or changed. Nitzan, 16.09: placing a box asks
 * whether the recipient signs or fills it in, or the sender does.
 */
export type AnchorRole = 'signer-signature' | 'sender-signature' | 'signer-text' | 'sender-text';

export function anchorRole(anchor: SignatureAnchor): AnchorRole {
  if (isSenderSignature(anchor)) return 'sender-signature';
  if (isSenderText(anchor)) return 'sender-text';
  return anchor.kind === 'text' ? 'signer-text' : 'signer-signature';
}

/**
 * 1.1 — the sender's drawing on this document, from the first box marked for
 * their signature that has one; null before they have drawn it.
 *
 * One drawing serves the whole document, so a second place marked for it is
 * signed as it is placed, instead of asking for the same signature again.
 */
export function senderSignatureOf(anchors: SignatureAnchor[]): string | null {
  return anchors.find((anchor) => isSenderSignature(anchor) && !anchor.annotation && anchor.image)?.image ?? null;
}

/*
 * The boxes below are built key by key, in the order the server writes one
 * back. The detail screen saves as the sender works and ignores the echo of its
 * own save by comparing JSON, so a box whose keys come back in another order
 * reads as somebody else's change, and the screen is put back to the server's
 * copy — along with whatever was typed while the save was out.
 */

/** Where a box is on the document, apart from what goes in it. */
export type BoxFrame = Pick<SignatureAnchor, 'page' | 'x' | 'y' | 'width' | 'height'>;

/**
 * A box for `role` at `frame`, among `anchors`.
 *
 * A signer's box keeps the `signer` it is given: a box changed from a signature
 * to a blank is still that signer's. A box of the sender's is no signer's.
 * Nothing put in the box for another option comes with it — a signer's blank
 * that arrived with the sender's words in it would be answered for them — and a
 * box for the sender's signature is signed with the drawing the document
 * already has, if it has one.
 */
function boxFor(frame: BoxFrame, role: AnchorRole, anchors: SignatureAnchor[], signer?: number): SignatureAnchor {
  const { page, x, y, width, height } = frame;
  const kind = role === 'signer-text' || role === 'sender-text' ? 'text' : 'signature';
  if (role === 'signer-signature' || role === 'signer-text') {
    return { page, kind, x, y, width, height, ...(signer === undefined ? {} : { signer }) };
  }
  const image = role === 'sender-signature' ? senderSignatureOf(anchors) : null;
  return { page, kind, x, y, width, height, filledBy: 'sender', ...(image ? { image } : {}) };
}

/** 1.1, redone — the list with a box placed at `frame`, as the option picked for it. */
export function addBox(anchors: SignatureAnchor[], frame: BoxFrame, role: AnchorRole): SignatureAnchor[] {
  return [...anchors, boxFor(frame, role, anchors)];
}

/**
 * 1.1, redone — the list with the box at `index` changed to `role` where it
 * stands.
 *
 * Picking what the box already is hands back the same list: its words, its
 * drawing and whose box it is all stay.
 */
export function withRole(anchors: SignatureAnchor[], index: number, role: AnchorRole): SignatureAnchor[] {
  const anchor = anchors[index];
  if (!anchor || anchorRole(anchor) === role) return anchors;
  const changed = boxFor(anchor, role, anchors, anchor.signer);
  return anchors.map((current, at) => (at === index ? changed : current));
}

/**
 * 1.1, redone — the list with `image` as the drawing on every box marked for
 * the sender's signature. There is one signature for the document, so drawing
 * it again anywhere signs every place it goes. The drawing is the last key of
 * such a box, where the spread leaves it or puts it.
 */
export function signAsSender(anchors: SignatureAnchor[], image: string): SignatureAnchor[] {
  return anchors.map((anchor) => (isSenderSignature(anchor) && !anchor.annotation ? { ...anchor, image } : anchor));
}

/**
 * 1.1, redone — the longest drawing the server keeps on a box, in data-URL
 * characters: the schema's own cap, worked out the same way.
 */
export const SENDER_SIGNATURE_MAX_CHARS =
  DOCUMENT_SIGNATURE_PREFIX.length + Math.ceil(LIMITS.maxSenderSignatureBytes / 3) * 4;

/** Longest-side budgets, sharpest first; textured photographs need a fallback. */
const SENDER_SIGNATURE_WIDTHS = [600, 400, 300, 240, 180, 128];

/**
 * 1.1, redone — the sender's drawing, made small enough for the server to keep.
 *
 * The pad records at the screen's pixel density, two or three times its own
 * width on a phone, and every place marked for the sender's signature carries a
 * copy. 600 pixels across is still sharp on a signature line; a drawing that
 * does not fit is reduced progressively, including below 300 for photo grain.
 * `encode` fits both dimensions inside its budget and preserves proportions.
 * Null when not even the smallest encoding fits; the byte cap stays unchanged.
 */
export function fitSenderSignature(encode: (maxWidth: number) => string): string | null {
  for (const width of SENDER_SIGNATURE_WIDTHS) {
    const drawing = encode(width);
    const payload = drawing.slice(DOCUMENT_SIGNATURE_PREFIX.length);
    const bytes = payload.length * 3 / 4 - (payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0);
    if (drawing.length <= SENDER_SIGNATURE_MAX_CHARS && bytes <= LIMITS.maxSenderSignatureBytes) return drawing;
  }
  return null;
}

/**
 * 1.1 — whether the signing page has a blank for this signer to type into.
 *
 * The page says so in a line of its own, and saying it to a signer whose page
 * has only the sender's words and other signers' blanks on it sends them
 * looking for a field they will not find.
 */
export function asksSignerToType(anchors: SignatureAnchor[], position: number): boolean {
  return anchors.some((anchor) => anchor.kind === 'text' && anchorSigner(anchor) === position);
}

/** Removing a recipient removes their requests and shifts later identities together, preserving sender ink. */
export function removeSignerAnchors(anchors:SignatureAnchor[],position:number):SignatureAnchor[] {
 return anchors.filter(anchor=>anchor.filledBy==='sender'||(anchor.signer??1)!==position).map(anchor=>anchor.filledBy!=='sender'&&(anchor.signer??1)>position?{...anchor,signer:(anchor.signer??1)-1}:anchor);
}
